import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { GuestProvider, useGuest } from "@/context/GuestContext";

import HomePage from "@/pages/public/HomePage";
import RoomsPage from "@/pages/public/RoomsPage";
import AmenitiesPage from "@/pages/public/AmenitiesPage";
import GalleryPage from "@/pages/public/GalleryPage";
import AttractionsPage from "@/pages/public/AttractionsPage";
import FaqPage from "@/pages/public/FaqPage";
import ContactPage from "@/pages/public/ContactPage";
import AvailabilityPage from "@/pages/public/AvailabilityPage";

import LoginPage from "@/pages/LoginPage";
import OwnerSetupPage from "@/pages/OwnerSetupPage";
import GuestCheckInPage from "@/pages/guest/GuestCheckInPage";
import GuestDashboard from "@/pages/guest/GuestDashboard";
import GuestMenuPage from "@/pages/guest/GuestMenuPage";
import GuestOrdersPage from "@/pages/guest/GuestOrdersPage";
import GuestServicesPage from "@/pages/guest/GuestServicesPage";
import GuestBillPage from "@/pages/guest/GuestBillPage";
import GuestFeedbackPage from "@/pages/guest/GuestFeedbackPage";
import GuestReviewPage from "@/pages/guest/GuestReviewPage";

import AdminDashboard from "@/pages/admin/AdminDashboard";
import KitchenDashboard from "@/pages/kitchen/KitchenDashboard";
import StaffDashboard from "@/pages/staff/StaffDashboard";

function StaffOnly({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center font-serif">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role) && user.role !== "SUPER_ADMIN") return <Navigate to="/login" replace />;
  return children;
}

function GuestOnly({ children }) {
  const { guest, loading } = useGuest();
  if (loading) return <div className="min-h-screen flex items-center justify-center font-serif">Loading…</div>;
  if (!guest) return <Navigate to="/guest/check-in" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <GuestProvider>
            <Toaster position="top-center" richColors />
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/rooms" element={<RoomsPage />} />
              <Route path="/amenities" element={<AmenitiesPage />} />
              <Route path="/gallery" element={<GalleryPage />} />
              <Route path="/attractions" element={<AttractionsPage />} />
              <Route path="/faq" element={<FaqPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/availability" element={<AvailabilityPage />} />

              <Route path="/login" element={<LoginPage />} />
              <Route path="/owner-setup" element={<OwnerSetupPage />} />

              <Route path="/guest/check-in" element={<GuestCheckInPage />} />
              <Route path="/guest/dashboard" element={<GuestOnly><GuestDashboard /></GuestOnly>} />
              <Route path="/guest/menu" element={<GuestOnly><GuestMenuPage /></GuestOnly>} />
              <Route path="/guest/orders" element={<GuestOnly><GuestOrdersPage /></GuestOnly>} />
              <Route path="/guest/services" element={<GuestOnly><GuestServicesPage /></GuestOnly>} />
              <Route path="/guest/bill" element={<GuestOnly><GuestBillPage /></GuestOnly>} />
              <Route path="/guest/feedback" element={<GuestFeedbackPage />} />
              <Route path="/guest/review" element={<GuestReviewPage />} />

              <Route path="/admin" element={<StaffOnly roles={["ADMIN", "MANAGER"]}><AdminDashboard /></StaffOnly>} />
              <Route path="/kitchen" element={<StaffOnly roles={["ADMIN", "MANAGER", "KITCHEN"]}><KitchenDashboard /></StaffOnly>} />
              <Route path="/staff" element={<StaffOnly roles={["ADMIN", "MANAGER", "STAFF"]}><StaffDashboard /></StaffOnly>} />
            </Routes>
          </GuestProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
